<template>
  <div class="nested-variable" :class="{ highlighted: isHighlighted }">
    <div class="nested-content" @click="toggle">
      <div v-if="canExpand" class="arrow-container" :class="{ expanded: expanded }">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M17.7364 9.3636C17.3849 9.01213 16.8151 9.01213 16.4636 9.36361L12 13.8272L7.53639 9.3636C7.18492 9.01213 6.61507 9.01213 6.2636 9.36361C5.91213 9.71508 5.91213 10.2849 6.2636 10.6364L11.3636 15.7364C11.7151 16.0879 12.285 16.0879 12.6364 15.7364L17.7364 10.6364C18.0879 10.2849 18.0879 9.71507 17.7364 9.3636Z"
            fill="#949DB8"
          />
        </svg>
      </div>
      <span v-else class="expand-placeholder"></span>

      <span class="nested-name">{{ field.name }}</span>
      <span class="nested-display">{{ displayValue }}</span>
    </div>

    <div v-if="expanded && canExpand" class="nested-children">
      <NestedVariable
        v-for="(nestedField, index) in nestedFields"
        :key="nestedField._uniqueId || `${field.name}_${index}_${depth}`"
        :field="nestedField"
        :values="values"
        :visited="nestedField.visited"
        :depth="depth + 1"
        :highlights="highlights"
        :frame-index="frameIndex"
        :path="[...path, field]"
      />
    </div>
  </div>
</template>

<script>
import highlightMixin from './highlightMixin.js';
import {
  hasResolvableNestedValues,
  formatValueDisplay,
  getNestedFields,
} from './stackTraceValueHelpers.js';

export default {
  name: 'NestedVariable',
  mixins: [highlightMixin],
  props: {
    field: {
      type: Object,
      required: true,
    },
    values: {
      type: Array,
      required: true,
    },
    visited: {
      type: Set,
      required: true,
    },
    depth: {
      type: Number,
      default: 0,
    },
    highlights: {
      type: Array,
      default: () => [],
    },
    frameIndex: {
      type: Number,
      required: true,
    },
    path: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      expanded: false,
    };
  },

  computed: {
    fieldValue() {
      if (!this.field.value_id) return null;
      return this.values.find((v) => v.id === this.field.value_id);
    },

    canExpand() {
      const MAX_DEPTH = 7;

      if (!this.fieldValue || this.depth >= MAX_DEPTH) {
        return false;
      }

      if (this.field.value_id && this.visited.has(this.field.value_id)) {
        return false;
      }

      return hasResolvableNestedValues(this.fieldValue, this.values);
    },
    currentPath() {
      return [...this.path, this.field];
    },

    isHighlighted() {
      return this.isPathHighlighted(this.frameIndex, this.currentPath);
    },

    shouldExpand() {
      return this.shouldExpandForHighlight(this.frameIndex, this.currentPath);
    },

    displayValue() {
      if (!this.fieldValue) return 'null';

      return formatValueDisplay(this.fieldValue);
    },

    nestedFields() {
      if (!this.canExpand) return [];

      const newVisited = new Set(this.visited);
      if (this.field.value_id) {
        newVisited.add(this.field.value_id);
      }

      const fieldsArray = getNestedFields(this.fieldValue);

      return fieldsArray.map((field, index) => ({
        ...field,
        visited: newVisited,
        _uniqueId: `${this.field.value_id}_${index}_${field.name}`,
      }));
    },
  },

  watch: {
    highlights: {
      handler() {
        this.expandIfHighlighted();
      },
      deep: true,
    },
  },

  mounted() {
    this.expandIfHighlighted();
  },

  methods: {
    expandIfHighlighted() {
      if (this.shouldExpand && this.canExpand) {
        this.expanded = true;
      }
    },

    toggle() {
      if (this.canExpand) {
        this.expanded = !this.expanded;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.nested-variable {
  &.highlighted {
    background-color: #feeae7;
  }
}

.nested-content {
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
}

.expand-placeholder {
  width: 24px;
  display: inline-block;
}

.nested-name {
  color: #0066cc;
  margin-right: 8px;
  font-weight: 500;
}

.nested-display {
  color: #333;
  word-break: break-word;
}

.nested-children {
  margin-left: 20px;
  border-left: 1px dotted #ccc;
  padding-left: 8px;
}

.arrow-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: transform 0.3s ease;

  &.expanded {
    transform: rotate(-180deg);
  }
}
</style>
